import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import partnerBadge from './partner-badge.png';
import logoHead from './logo-head.png';
import feat1 from './feat-1.jpg';
import feat2 from './feat-2.jpg';
import feat3 from './feat-3.jpg';

import { Button, Heading, HelpText, Link } from '@mm/components';

import { CheckCircleIcon } from '@mm/icons/general';
import { UTMTags } from '@mm/hooks/src/use-utm-tags';

import css from './AmbassadorLanding.module.css';
import { colors } from '../../../../../editor/features/beta/styles/colors';
import { Logo } from '../../../../../layout/BotBuilderSidebar/Logo/Logo';
import { useAffiliateData } from './useAffiliateData/use-affiliate-data';
import FacebookLoginButton, {
  Props as FacebookLoginButtonProps,
} from '../../../../../common/buttons/FacebookLoginButton/FacebookLoginButton';
import { useSignup } from '../../SignUpForm/SignUpForm';
import { randomId } from '../../../../../common/helpers/utils';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { useFacebookSessionCRUDActions } from '@mm/state/actions/FacebookSessionCRUDActions';
import { SessionObject } from '@mm/state/interfaces/session';
import commonHandler from '@mm/state/errorHandlers/common-handler';
import { useDispatch } from 'react-redux';
import { StoreState } from '@mm/state/store-interfaces';
import { Dispatch } from 'redux';
import { GenericFooter } from '../../GenericFooter/GenericFooter';

type Props = {
  className?: string;
  children?: any;
  affiliateId?: string;
};

export const AmbassadorLanding = ({ className, affiliateId = '' }: Props) => {
  const utmObj = UTMTags.get();

  const dispatch: Dispatch<StoreState> = useDispatch();
  const { create: createFBSession } = useFacebookSessionCRUDActions();

  const getAffiliateData = useAffiliateData();
  const { updateUserAttributes } = useUsersCRUDActions();

  const data = useMemo(
    () => getAffiliateData(affiliateId),
    [getAffiliateData, affiliateId],
  );

  const onSuccess = useCallback<FacebookLoginButtonProps['onSuccess']>(
    async ({ facebookToken }) => {
      try {
        const { entity }: { entity: SessionObject } = await createFBSession({
          facebookToken,
          inviteToken: '',
          userAttributes: {
            signupForm: '',
            ...utmObj,
          },
        });
        const userId = entity?.id;
        const companyId = entity?.userAttributes.activeCompanyId;

        if (entity?.newlyCreatedUser) {
          await updateUserAttributes(companyId, userId, {
            showPaywall: true,
          });
        }
      } catch (err) {
        await commonHandler(dispatch)(err);
      }
    },
    [],
  );

  const cta = (
    <div className={classNames(css.section, css.cta)}>
      <div className={css.container}>
        <span className={css.headline}>
          Accelerate lead generation and sales funnels with Customers.ai and{' '}
          {data?.company}
        </span>

        <span className={css.subtitle}>{data?.subtitle}</span>

        <div className={css.buttons}>
          <FacebookLoginButton
            className={css.button}
            onSuccess={onSuccess}
            label="Sign up with Facebook"
          />
          {/*<span className={css.divider}>or</span>*/}
          {/*<Button css={{ container: css.button }} color="green">*/}
          {/*  Signup with email*/}
          {/*</Button>*/}
        </div>

        <Link
          href="https://customers.ai/help/article/95khfiqc43-why-do-i-have-to-sign-up-with-facebook"
          isBlank
        >
          Why do I have to sign up with Facebook?
        </Link>

        {data?.code && <HelpText className={css.code}>{data?.code}</HelpText>}
      </div>
    </div>
  );

  return (
    <div className={classNames(className, css.wrapper)}>
      <div className={css.container}>
        <div className={css.navbar}>
          <Logo className={css.logo} />
          <div className={css.badge}>
            <img src={partnerBadge} alt="Facebook Business Partner" />
          </div>
          <span className={css.text}>
            Meta Business Partner USED BY {data?.name.toUpperCase()}
          </span>
        </div>
      </div>

      <div className={css.section}>
        <div className={css.container}>
          <span className={css.headline}>
            {data?.company}'s Favorite Chat Platform to Engage & Convert
            Prospects Across Instagram, SMS, Web Chat & Facebook Messaging
          </span>

          <div className={css.testimonial}>
            <div className={css.avatar}>
              <img src={data?.avatar} alt={data?.name} />
            </div>
            <div className={css.info}>
              <span className={css.name}>{data?.name}</span>
              <span className={css.position}>{data?.position}</span>
              <span className={css.mark}>“</span>
              <p className={css.quote}>{data?.quote}</p>
            </div>
          </div>
        </div>
      </div>

      {cta}

      <div className={css.section}>
        <div className={css.container}>
          <div className={css.feature}>
            <span className={css.headline}>What Is Customers.ai?</span>
            <div className={css.image}>
              <img src={feat1} alt="What Is Customers.ai?" />
            </div>
            <div className={css.description}>
              <span className={css.headline}>What Is Customers.ai?</span>
              <p className={css.paragraph}>
                Customers.ai is the pioneer of OmniChatⓇ — a multi-user,
                multi-channel inbox, campaign and automation builder for
                customer chat.
              </p>
              <p className={css.paragraph}>
                Startup Edition by Customers.ai is the only platform you need to
                engage and convert more customers in the most popular messaging
                channels: Instagram, SMS, Facebook Messenger and website chat.
              </p>
              <p className={css.paragraph}>
                And Customers.ai is an official Meta Business Partner for
                messaging tools, and Instagram and Facebook approved automation
                tools dedicated to powering audience growth, instant engagement,
                traffic generation, and customer support.
              </p>
            </div>
          </div>
          <div className={css.feature}>
            <span className={css.headline}>
              Who Is Startup Edition by Customers.ai For?
            </span>
            <div className={css.image}>
              <img
                src={feat2}
                alt="Who Is Startup Edition by Customers.ai For?"
              />
            </div>

            <div className={css.description}>
              <span className={css.headline}>
                Who Is Startup Edition by Customers.ai For?
              </span>
              <p className={css.paragraph}>
                Startup Edition is a powerful OmniChatⓇ marketing platform for
                B2C marketers and advertisers at companies with 2 or more
                full-time marketers.
              </p>
              <span className={css.paragraph}>
                <ul>
                  <li>
                    <CheckCircleIcon color={colors.green} /> Generate leads 24/7
                    with chat automation for Instagram, SMS, web chat and
                    Messenger.
                  </li>
                  <li>
                    <CheckCircleIcon color={colors.green} /> Launch paid and
                    organic campaigns for lead generation and re-engagement from
                    a streamlined platform.
                  </li>
                  <li>
                    <CheckCircleIcon color={colors.green} /> Save your team time
                    with an intuitive automation builder, integrations, and
                    analytics to tie your efforts to ROI.
                  </li>
                </ul>
              </span>
              <p className={css.paragraph}>
                And Customers.ai is an official Meta Business Partner for
                messaging tools, and Instagram and Facebook approved automation
                tools dedicated to powering audience growth, instant engagement,
                traffic generation, and customer support.
              </p>
            </div>
          </div>
          <div className={css.feature}>
            <span className={css.headline}>
              What's Included in Startup Edition by Customers.ai?
            </span>
            <div className={css.image}>
              <img
                src={feat3}
                alt="What's Included in Startup Edition by Customers.ai?"
              />
            </div>
            <div className={css.description}>
              <span className={css.headline}>
                What's Included in Startup Edition by Customers.ai?
              </span>
              <p className={css.paragraph}>
                Startup Edition by Customers.ai's full-suite chat marketing
                platform for Instagram, SMS, Facebook Messenger and your website
                includes:
              </p>
              <span className={css.paragraph}>
                <ul>
                  <li>
                    <CheckCircleIcon color={colors.green} />
                    Unified inbox to view, filter and respond to all customer
                    chat conversations across messaging channels
                  </li>
                  <li>
                    <CheckCircleIcon color={colors.green} />
                    1,000 credits/month
                  </li>
                  <li>
                    <CheckCircleIcon color={colors.green} />1 user seat with
                    optional seat add-ons
                  </li>
                  <li>
                    <CheckCircleIcon color={colors.green} />
                    Advanced bot automations
                  </li>
                  <li>
                    <CheckCircleIcon color={colors.green} />
                    Office hours autoresponder
                  </li>
                  <li>
                    <CheckCircleIcon color={colors.green} />
                    Zapier integrations
                  </li>
                  <li>
                    <CheckCircleIcon color={colors.green} />
                    Facebook Messenger and Instagram Direct messaging ads
                  </li>
                  <li>
                    <CheckCircleIcon color={colors.green} />
                    Conversation and contact analytics
                  </li>
                </ul>
              </span>
            </div>
          </div>
        </div>
      </div>

      {cta}

      <GenericFooter showNav />
    </div>
  );
};
